import React, { useState } from 'react';
import { urlDoArquivo } from '../../services/upload';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Abastecimento } from '../../types';
import { abastecimentosService, AbastecimentoResponse } from '../../services/abastecimentos';
import { useConfirm } from '../../components/ConfirmDialog';
import { parseApiDate, formatKilometers } from '../../utils/formatters';

const AbastecimentoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('detalhes');

  const { data, isLoading, error } = useQuery<AbastecimentoResponse>({
    queryKey: ['abastecimento', id],
    queryFn: () => abastecimentosService.getAbastecimento(Number(id!)),
    enabled: Boolean(id)
  });

  const deleteMutation = useMutation({
    mutationFn: () => abastecimentosService.deleteAbastecimento(Number(id!)),
    onSuccess: () => {
      toast.success('Abastecimento excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['abastecimentos'] });
      // Redirecionar para a lista
      window.location.href = '/abastecimentos';
    },
    onError: () => {
      toast.error('Erro ao excluir abastecimento');
    }
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Excluir abastecimento',
      message: 'Tem certeza que deseja excluir este abastecimento? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (ok) {
      deleteMutation.mutate();
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getTipoCombustivelColor = (tipo: string) => {
    switch (tipo) {
      case 'gasolina': return 'bg-red-100 text-red-800';
      case 'etanol': return 'bg-green-100 text-green-800';
      case 'diesel': return 'bg-blue-100 text-blue-800';
      case 'gnv': return 'bg-purple-100 text-purple-800';
      case 'flex': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="w-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Abastecimento não encontrado</h1>
          <Link
            to="/abastecimentos"
            className="text-blue-600 hover:text-blue-800"
          >
            Voltar para a lista
          </Link>
        </div>
      </div>
    );
  }

  const abastecimento: Abastecimento = data.data;

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Detalhes do abastecimento</h1>
            <p className="text-gray-600">
              Registrado em {formatDate(abastecimento.data_abastecimento)}
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to={`/abastecimentos/${abastecimento.id}/editar`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Editar
            </Link>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('detalhes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'detalhes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Detalhes
            </button>
            <button
              onClick={() => setActiveTab('veiculo')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'veiculo'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Veículo
            </button>
            <button
              onClick={() => setActiveTab('motorista')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'motorista'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Motorista
            </button>
          </nav>
        </div>

        {/* Conteúdo das Tabs */}
        {activeTab === 'detalhes' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informações do Abastecimento */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">Informações do Abastecimento</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Data:</span>
                  <span className="font-medium">{formatDate(abastecimento.data_abastecimento)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quilometragem:</span>
                  <span className="font-medium">{formatKilometers(abastecimento.quilometragem)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tipo de Combustível:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoCombustivelColor(abastecimento.tipo_combustivel)}`}>
                    {abastecimento.tipo_combustivel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tanque Cheio:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    abastecimento.tanque_cheio ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {abastecimento.tanque_cheio ? '✓ Sim' : '✗ Não'}
                  </span>
                </div>
                {abastecimento.nota_fiscal && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nota Fiscal:</span>
                    <span className="font-medium">{abastecimento.nota_fiscal}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Informações Financeiras */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">Informações Financeiras</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Litros:</span>
                  <span className="font-medium">{formatNumber(abastecimento.litros)} L</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Preço por Litro:</span>
                  <span className="font-medium">{formatCurrency(abastecimento.preco_litro)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold">
                  <span className="text-gray-900">Valor Total:</span>
                  <span className="text-green-600">{formatCurrency(abastecimento.valor_total)}</span>
                </div>
                {abastecimento.consumo && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Consumo:</span>
                    <span className="font-medium">{formatNumber(abastecimento.consumo)} km/L</span>
                  </div>
                )}
                {abastecimento.km_rodados && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">KM Rodados:</span>
                    <span className="font-medium">{formatKilometers(abastecimento.km_rodados)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Informações do Posto */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">Informações do Posto</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Posto:</span>
                  <span className="font-medium">{abastecimento.posto}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cidade:</span>
                  <span className="font-medium">{abastecimento.cidade}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Estado:</span>
                  <span className="font-medium">{abastecimento.estado}</span>
                </div>
                {(abastecimento.comprovantes?.length ?? 0) > 0 && (
                  <div>
                    <span className="text-gray-600">
                      Comprovantes ({abastecimento.comprovantes!.length}):
                    </span>
                    <ul className="mt-2 space-y-1">
                      {abastecimento.comprovantes!.map((caminho) => (
                        <li key={caminho} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-700 truncate" title={caminho}>
                            {caminho.split('/').pop()}
                          </span>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <a
                              href={urlDoArquivo(caminho)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              Ver
                            </a>
                            <span className="text-gray-400">|</span>
                            <a
                              href={urlDoArquivo(caminho)}
                              download
                              className="text-green-600 hover:text-green-800 underline"
                            >
                              Download
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Observações */}
            {abastecimento.observacoes && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Observações</h2>
                <p className="text-gray-700">{abastecimento.observacoes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'veiculo' && abastecimento.veiculo && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações do Veículo</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Placa:</span>
                  <span className="font-medium">{abastecimento.veiculo.placa}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Marca:</span>
                  <span className="font-medium">{abastecimento.veiculo.marca}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Modelo:</span>
                  <span className="font-medium">{abastecimento.veiculo.modelo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ano:</span>
                  <span className="font-medium">{abastecimento.veiculo.ano_fabricacao}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cor:</span>
                  <span className="font-medium">{abastecimento.veiculo.cor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Categoria:</span>
                  <span className="font-medium">{abastecimento.veiculo.categoria}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    abastecimento.veiculo.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {abastecimento.veiculo.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quilometragem Atual:</span>
                  <span className="font-medium">{abastecimento.veiculo.quilometragem} km</span>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link
                to={`/veiculos/${abastecimento.veiculo.id}`}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Ver detalhes completos do veículo →
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'motorista' && abastecimento.motorista && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações do Motorista</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Nome:</span>
                  <span className="font-medium">{abastecimento.motorista.nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CPF:</span>
                  <span className="font-medium">{abastecimento.motorista.cpf}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CNH:</span>
                  <span className="font-medium">{abastecimento.motorista.cnh}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Categoria CNH:</span>
                  <span className="font-medium">{abastecimento.motorista.categoria_cnh}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Telefone:</span>
                  <span className="font-medium">{abastecimento.motorista.telefone || 'Não informado'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{abastecimento.motorista.email || 'Não informado'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    abastecimento.motorista.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {abastecimento.motorista.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vencimento CNH:</span>
                  <span className="font-medium">
                    {abastecimento.motorista.vencimento_cnh ? formatDate(abastecimento.motorista.vencimento_cnh) : 'Não informado'}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link
                to={`/motoristas/${abastecimento.motorista.id}`}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Ver detalhes completos do motorista →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbastecimentoDetail;
